// OPFS（Origin Private File System）でメディア本体を保存する。
// IndexedDB に GB 級の Blob を入れるより OPFS の方が iOS/WebKit で安定して扱える。
// レイアウト: media/<hash>.<ext>

const SUBDIR = 'media'

async function mediaDir(create = false) {
  const root = await navigator.storage.getDirectory()
  return root.getDirectoryHandle(SUBDIR, { create })
}

function fileName(hash, ext) {
  return ext ? `${hash}.${ext.replace(/^\./, '')}` : hash
}

// 既存ファイルのハンドルを探す（拡張子不明でも hash 前方一致で見つける）
async function findHandle(hash) {
  let dir
  try {
    dir = await mediaDir(false)
  } catch {
    return null
  }
  for await (const [name, handle] of dir.entries()) {
    if (handle.kind === 'file' && (name === hash || name.startsWith(hash + '.'))) {
      return handle
    }
  }
  return null
}

// Blob/ArrayBuffer をストリーム保存
export async function saveMedia(hash, ext, blob) {
  const dir = await mediaDir(true)
  const handle = await dir.getFileHandle(fileName(hash, ext), { create: true })
  const w = await handle.createWritable()
  await w.write(blob)
  await w.close()
  return fileName(hash, ext)
}

// Web ストリームを OPFS へ逐次書き込み（GB級でもメモリを食わない）。
// onProgress(loaded, total) を都度呼ぶ。途中失敗時は書きかけを破棄する。
export async function saveMediaStream(hash, ext, webStream, total, onProgress) {
  const dir = await mediaDir(true)
  const handle = await dir.getFileHandle(fileName(hash, ext), { create: true })
  const w = await handle.createWritable()
  const reader = webStream.getReader()
  let loaded = 0
  try {
    for (;;) {
      const { done, value } = await reader.read()
      if (done) break
      await w.write(value)
      loaded += value.byteLength
      if (onProgress) onProgress(loaded, total)
    }
    await w.close()
  } catch (e) {
    try {
      await w.abort()
    } catch {
      /* noop */
    }
    throw e
  }
  return fileName(hash, ext)
}

// 再生用に blob: URL を得る（使い終わったら revokeObjectURL する）
export async function mediaURL(hash) {
  const handle = await findHandle(hash)
  if (!handle) return null
  const file = await handle.getFile()
  return URL.createObjectURL(file)
}

export async function hasMedia(hash) {
  return (await findHandle(hash)) != null
}

// アップロード用に File を得る（無ければ null）。File は Blob なので fetch body にそのまま使える。
export async function getMediaFile(hash) {
  const handle = await findHandle(hash)
  if (!handle) return null
  return handle.getFile()
}

export async function deleteMedia(hash) {
  const handle = await findHandle(hash)
  if (!handle) return false
  const dir = await mediaDir(false)
  await dir.removeEntry(handle.name)
  return true
}

// 使用容量の概算（設定画面などで表示用）
export async function storageEstimate() {
  if (!navigator.storage?.estimate) return null
  const { usage, quota } = await navigator.storage.estimate()
  return { usage, quota }
}
